export type SearchFilter = {
  text?: string[];
  subject?: string[];
  body?: string[];
  from?: string[];
  to?: string[];
  cc?: string[];
  participants?: string[];
  toOrCc?: string[];
  attachmentName?: string[];
  receivedFrom?: string;
  receivedTo?: string;
  sentFrom?: string;
  sentTo?: string;
  hasAttachments?: boolean;
  unread?: boolean;
  flagged?: boolean;
  importance?: 'low' | 'normal' | 'high';
  categories?: string[];
  sizeMin?: number;
  sizeMax?: number;
  folder?: string;
};

export type QueryNode = { op: 'term'; field: keyof SearchFilter; value: string } | { op: 'and' | 'or'; left: QueryNode; right: QueryNode } | { op: 'not'; child: QueryNode };
export type SearchQueryError = { code: 'unsupported_keyword' | 'invalid_value' | 'syntax'; position: number; keyword?: string };
export type ParseResult = { ok: true; ast?: QueryNode } | { ok: false; error: SearchQueryError };

const fields: Record<string, keyof SearchFilter> = {
  from: 'from', to: 'to', cc: 'cc', participants: 'participants', toorcc: 'toOrCc', subject: 'subject', body: 'body',
  hasattachment: 'hasAttachments', hasattachments: 'hasAttachments', attachment: 'attachmentName', attachments: 'attachmentName',
  isread: 'unread', read: 'unread', isflagged: 'flagged', hasflag: 'flagged', importance: 'importance', category: 'categories',
  size: 'sizeMin', received: 'receivedFrom', date: 'receivedFrom', sent: 'sentFrom', folder: 'folder',
};
type LocalePack = { messages?: { webmail?: { searchQueryAliases?: Record<string, string> } } };
const packAliases = (pack: unknown): Record<string, string> => (pack as LocalePack).messages?.webmail?.searchQueryAliases ?? {};
const EN_ALIASES = packAliases(enPack);
const DE_ALIASES = packAliases(dePack);
const normalizeKeyword = (word: string): string => word.toLocaleLowerCase().replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss');
function keywordMap(locale: 'en' | 'de'): Record<string, string> {
  const map: Record<string, string> = {};
  for (const [canonical, aliases] of Object.entries(locale === 'de' ? { ...EN_ALIASES, ...DE_ALIASES } : EN_ALIASES)) {
    for (const alias of aliases.split(',')) map[normalizeKeyword(alias.trim())] = canonical;
  }
  return map;
}
type Token = { kind: 'word' | 'quoted' | 'colon' | 'lparen' | 'rparen' | 'and' | 'or' | 'not' | 'minus' | 'eof'; value: string; position: number };
class SearchParseFailure extends Error {
  constructor(readonly detail: SearchQueryError) {
    super(detail.code);
  }
}

function failParse(detail: SearchQueryError): never {
  throw new SearchParseFailure(detail);
}

function lex(source: string, keywords: Record<string, string>): Token[] | SearchQueryError {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const c = source[i] ?? '';
    if (/\s/.test(c)) { i++; continue; }
    if (c === '(' || c === ')') { tokens.push({ kind: c === '(' ? 'lparen' : 'rparen', value: c, position: i++ }); continue; }
    if (c === ':') { tokens.push({ kind: 'colon', value: c, position: i++ }); continue; }
    if (c === '-') { tokens.push({ kind: 'minus', value: c, position: i++ }); continue; }
    if (c === '"') {
      const start = i++;
      let value = '';
      while (i < source.length && source[i] !== '"') {
        if (source[i] === '\\' && source[i + 1] === '"') { value += '"'; i += 2; } else value += source[i++];
      }
      if (source[i] !== '"') return { code: 'syntax', position: start };
      i++;
      tokens.push({ kind: 'quoted', value, position: start });
      continue;
    }
    const start = i;
    while (i < source.length && !/\s|[():]/.test(source[i] ?? '')) i++;
    const value = source.slice(start, i);
    const word = value.toLowerCase();
    const canonical = keywords[normalizeKeyword(word)];
    const kind = canonical && ['and', 'or', 'not'].includes(canonical) ? canonical : 'word';
    tokens.push({ kind: kind as Token['kind'], value, position: start });
  }
  tokens.push({ kind: 'eof', value: '', position: source.length });
  return tokens;
}

export function parseSearchQuery(source: string, locale: 'en' | 'de' = 'en'): ParseResult {
  const keywords = keywordMap(locale);
  const lexed = lex(source, keywords);
  if (!Array.isArray(lexed)) return { ok: false, error: lexed };
  const tokens = lexed;
  let cursor = 0;
  const peek = (): Token => tokens[cursor] ?? { kind: 'eof', value: '', position: source.length };
  const take = (): Token => { const t = peek(); cursor++; return t; };
  const validate = (key: keyof SearchFilter, value: string, position: number): void => {
    if (key === 'hasAttachments' || key === 'unread' || key === 'flagged') {
      const v = value.toLowerCase();
      const yes = ['yes', 'true', 'ja', '1'];
      const no = ['no', 'false', 'nein', '0'];
      if (!yes.includes(v) && !no.includes(v)) failParse({ code: 'invalid_value', position });
      return;
    }
    if (key === 'importance') {
      const valueMap: Record<string, 'low' | 'normal' | 'high'> = { low: 'low', niedrig: 'low', normal: 'normal', high: 'high', hoch: 'high' };
      const normalized = valueMap[value.toLowerCase()];
      if (!normalized) failParse({ code: 'invalid_value', position });
      return;
    }
    if (key === 'receivedFrom' || key === 'sentFrom') {
      if (!/^(?:(?:>=|>)\d{4}-\d{2}-\d{2}|\d{4}-\d{2}-\d{2}(?:\.\.\d{4}-\d{2}-\d{2})?|today|yesterday|last week)$/i.test(value)) failParse({ code: 'invalid_value', position });
      return;
    }
    if (key === 'sizeMin') {
      const size = byteSize(value);
      if (!size || (size.max !== undefined && size.max < size.min)) failParse({ code: 'invalid_value', position });
      return;
    }
  };
  const parsePrimary = (): QueryNode | undefined => {
    const token = peek();
    if (token.kind === 'lparen') {
      take();
      const nested = parseOr();
      if (peek().kind !== 'rparen') failParse({ code: 'syntax', position: peek().position });
      take();
      return nested;
    }
    if (token.kind !== 'word' && token.kind !== 'quoted') return undefined;
    const value = take();
    let key: keyof SearchFilter = 'text';
    let term = value.value;
    if (peek().kind === 'colon') {
      take();
      const aliasKey = normalizeKeyword(value.value);
      const canonical = keywords[aliasKey] ?? aliasKey;
      const v = peek();
      if (v.kind !== 'word' && v.kind !== 'quoted') failParse({ code: 'syntax', position: v.position });
      const valueToken = take();
      term = valueToken.value;
      if (canonical === 'is') {
        const state = normalizeKeyword(term);
        const states: Record<string, { field: keyof SearchFilter; value: string }> = {
          unread: { field: 'unread', value: 'no' }, read: { field: 'unread', value: 'yes' },
          flagged: { field: 'flagged', value: 'yes' }, unflagged: { field: 'flagged', value: 'no' },
        };
        const mapped = states[state];
        if (!mapped) failParse({ code: 'invalid_value', position: valueToken.position });
        key = mapped.field;
        term = mapped.value;
      } else {
        const field = fields[canonical];
        if (!field) failParse({ code: 'unsupported_keyword', position: value.position, keyword: value.value });
        key = field;
      }
      validate(key, term, valueToken.position);
    }
    if (key === 'text') validate(key, term, value.position);
    return { op: 'term', field: key, value: term };
  };
  const parseUnary = (): QueryNode | undefined => {
    if (peek().kind === 'not' || peek().kind === 'minus') {
      take();
      const child = parseUnary();
      if (!child) failParse({ code: 'syntax', position: peek().position });
      return { op: 'not', child };
    }
    return parsePrimary();
  };
  const parseAnd = (): QueryNode | undefined => {
    let left = parseUnary();
    while (left && ['and', 'word', 'quoted', 'lparen', 'minus', 'not'].includes(peek().kind)) {
      if (peek().kind === 'and') take();
      const right = parseUnary();
      if (!right) failParse({ code: 'syntax', position: peek().position });
      left = { op: 'and', left, right };
    }
    return left;
  };
  const parseOr = (): QueryNode | undefined => {
    let left = parseAnd();
    while (left && peek().kind === 'or') {
      take();
      const right = parseAnd();
      if (!right) failParse({ code: 'syntax', position: peek().position });
      left = { op: 'or', left, right };
    }
    return left;
  };
  try {
    const ast = parseOr();
    if (peek().kind !== 'eof') failParse({ code: 'syntax', position: peek().position });
    return { ok: true, ...(ast && { ast }) };
  } catch (err) {
    if (err instanceof SearchParseFailure) return { ok: false, error: err.detail };
    throw err;
  }
}

export interface SearchDocument {
  folderName?: string;
  subject: string;
  body?: string;
  from?: { name?: string | null; email: string } | null;
  to?: readonly { name?: string | null; email: string }[];
  cc?: readonly { name?: string | null; email: string }[];
  sentAt?: string | null;
  attachmentNames?: readonly string[];
  categories?: readonly string[];
  receivedAt?: string | null;
  isRead?: boolean;
  flagged?: boolean;
  hasAttachments?: boolean;
  importance?: string | null;
  size?: number | null;
}

const contains = (value: string | null | undefined, needle: string): boolean => (value ?? '').toLocaleLowerCase().includes(needle.toLocaleLowerCase());
const addressContains = (entries: readonly { name?: string | null; email: string }[] | undefined, needle: string): boolean =>
  (entries ?? []).some((entry) => contains(entry.name, needle) || contains(entry.email, needle));
const addressOne = (entry: { name?: string | null; email: string } | null | undefined, needle: string): boolean =>
  entry !== null && entry !== undefined && (contains(entry.name, needle) || contains(entry.email, needle));

function byteSize(raw: string): { min: number; max?: number; exclusive?: boolean } | undefined {
  const bytes = (n: string, unit = '') => Math.floor(Number(n) * ({ '': 1, kb: 1024, mb: 1048576, gb: 1073741824 }[unit.toLowerCase()] ?? 1));
  const range = /^(\d+(?:\.\d+)?)(kb|mb|gb)?\.\.(\d+(?:\.\d+)?)(kb|mb|gb)?$/i.exec(raw);
  if (range?.[1] && range[3]) return { min: bytes(range[1], range[2]), max: bytes(range[3], range[4]) };
  const single = /^(>=|>)?(\d+(?:\.\d+)?)(kb|mb|gb)?$/i.exec(raw);
  return single?.[2] ? { min: bytes(single[2], single[3]), exclusive: single[1] === '>' } : undefined;
}

function dateBounds(raw: string, now: Date): { min?: number; max?: number; exclusive?: boolean } | undefined {
  const comparison = /^(>=|>)(\d{4}-\d{2}-\d{2})$/.exec(raw);
  if (comparison?.[2]) return { min: Date.parse(`${comparison[2]}T00:00:00Z`), exclusive: comparison[1] === '>' };
  const range = /^(\d{4}-\d{2}-\d{2})(?:\.\.(\d{4}-\d{2}-\d{2}))?$/.exec(raw);
  if (range?.[1]) return { min: Date.parse(`${range[1]}T00:00:00Z`), max: Date.parse(`${range[2] ?? range[1]}T23:59:59.999Z`) };
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  if (/^today$/i.test(raw)) return { min: today, max: today + 86_399_999 };
  if (/^yesterday$/i.test(raw)) return { min: today - 86_400_000, max: today - 1 };
  if (/^last week$/i.test(raw)) return { min: today - 7 * 86_400_000, max: today - 1 };
  return undefined;
}

function termMatches(field: keyof SearchFilter, raw: string, doc: SearchDocument, now: Date): boolean {
  switch (field) {
    case 'text': return [doc.subject, doc.body, doc.from?.name, doc.from?.email, ...(doc.to ?? []).flatMap((a) => [a.name ?? '', a.email]), ...(doc.cc ?? []).flatMap((a) => [a.name ?? '', a.email]), ...(doc.attachmentNames ?? [])].some((v) => contains(v, raw));
    case 'subject': return contains(doc.subject, raw);
    case 'body': return contains(doc.body, raw);
    case 'from': return addressOne(doc.from, raw);
    case 'to': return addressContains(doc.to, raw);
    case 'cc': return addressContains(doc.cc, raw);
    case 'participants': return addressOne(doc.from, raw) || addressContains(doc.to, raw) || addressContains(doc.cc, raw);
    case 'toOrCc': return addressContains(doc.to, raw) || addressContains(doc.cc, raw);
    case 'hasAttachments': return doc.hasAttachments === ['yes', 'true', 'ja', '1'].includes(raw.toLowerCase());
    case 'attachmentName': return (doc.attachmentNames ?? []).some((name) => contains(name, raw));
    case 'unread': return doc.isRead === ['yes', 'true', 'ja', '1'].includes(raw.toLowerCase());
    case 'flagged': return doc.flagged === ['yes', 'true', 'ja', '1'].includes(raw.toLowerCase());
    case 'importance': return contains(doc.importance, ({ low: 'Low', niedrig: 'Low', normal: 'Normal', high: 'High', hoch: 'High' } as Record<string, string>)[raw.toLowerCase()] ?? raw);
    case 'categories': return (doc.categories ?? []).some((category) => contains(category, raw));
    case 'sizeMin': {
      const size = byteSize(raw);
      if (doc.size == null || size === undefined) return false;
      return size.max === undefined ? (size.exclusive ? doc.size > size.min : doc.size >= size.min) : doc.size >= size.min && doc.size <= size.max;
    }
    case 'sizeMax': return doc.size !== null && doc.size !== undefined && doc.size <= Number(raw);
    case 'receivedFrom': {
      const bounds = dateBounds(raw, now);
      const received = Date.parse(doc.receivedAt ?? '');
      if (!bounds || !Number.isFinite(received)) return false;
      return received >= (bounds.min ?? Number.NEGATIVE_INFINITY) && received <= (bounds.max ?? Number.POSITIVE_INFINITY) && !(bounds.exclusive && received === bounds.min);
    }
    case 'receivedTo': return Date.parse(doc.receivedAt ?? '') <= Date.parse(`${raw}T23:59:59.999Z`);
    case 'sentFrom': {
      const bounds = dateBounds(raw, now);
      const sent = Date.parse(doc.sentAt ?? '');
      if (!bounds || !Number.isFinite(sent)) return false;
      return sent >= (bounds.min ?? Number.NEGATIVE_INFINITY) && sent <= (bounds.max ?? Number.POSITIVE_INFINITY) && !(bounds.exclusive && sent === bounds.min);
    }
    case 'sentTo': return Date.parse(doc.sentAt ?? '') <= Date.parse(`${raw}T23:59:59.999Z`);
    case 'folder': return contains(doc.folderName, raw);
    default: return false;
  }
}

export function matchesSearchQuery(ast: QueryNode | undefined, doc: SearchDocument, now = new Date()): boolean {
  if (!ast) return true;
  if (ast.op === 'term') return termMatches(ast.field, ast.value, doc, now);
  if (ast.op === 'not') return !matchesSearchQuery(ast.child, doc, now);
  return ast.op === 'and' ? matchesSearchQuery(ast.left, doc, now) && matchesSearchQuery(ast.right, doc, now) : matchesSearchQuery(ast.left, doc, now) || matchesSearchQuery(ast.right, doc, now);
}
import enPack from '@platform/i18n/packs/en.json' with { type: 'json' };
import dePack from '@platform/i18n/packs/de.json' with { type: 'json' };

/** Words a result should be highlighted for, per place: subject lines, and bodies/previews. */
export interface HighlightTerms {
  subject: string[];
  body: string[];
}

/**
 * The positive free-text, `subject:` and `body:` values of a query (negated terms never matched
 * anything, so they are not highlighted). Free text counts for both places.
 */
export function highlightTerms(ast: QueryNode | undefined): HighlightTerms {
  const terms: HighlightTerms = { subject: [], body: [] };
  const walk = (node: QueryNode, negated: boolean): void => {
    if (node.op === 'not') walk(node.child, !negated);
    else if (node.op === 'term') {
      if (negated) return;
      if (node.field === 'text' || node.field === 'subject') terms.subject.push(node.value);
      if (node.field === 'text' || node.field === 'body') terms.body.push(node.value);
    } else {
      walk(node.left, negated);
      walk(node.right, negated);
    }
  };
  if (ast) walk(ast, false);
  return terms;
}

/** Operators offered by search autocomplete, in display order. */
export const SEARCH_OPERATORS = [
  'from', 'to', 'cc', 'participants', 'subject', 'body', 'hasattachment', 'attachment', 'isread', 'isflagged',
  'importance', 'category', 'size', 'received', 'sent', 'folder',
] as const;
export type SearchOperator = (typeof SEARCH_OPERATORS)[number];

/** The keyword a user types for an operator in `locale` (first alias, e.g. `von` for `from` in German). */
export function operatorKeyword(operator: SearchOperator, locale: 'en' | 'de'): string {
  const aliases = (locale === 'de' ? { ...EN_ALIASES, ...DE_ALIASES } : EN_ALIASES)[operator] ?? operator;
  return aliases.split(',')[0]?.trim() ?? operator;
}

/** Operators whose keyword (or English name) starts with `prefix`, ignoring case and umlauts; complete keywords are skipped. */
export function matchOperators(prefix: string, locale: 'en' | 'de'): Array<{ operator: SearchOperator; keyword: string }> {
  const needle = normalizeKeyword(prefix);
  if (!needle) return [];
  return SEARCH_OPERATORS.flatMap((operator) => {
    const keyword = operatorKeyword(operator, locale);
    const names = keyword === operator ? [keyword] : [keyword, operator];
    return names.some((name) => normalizeKeyword(name).startsWith(needle)) && normalizeKeyword(keyword) !== needle ? [{ operator, keyword }] : [];
  });
}

/** Canonical operator of a typed keyword (`von` -> `from`), or undefined. */
export function operatorOf(keyword: string, locale: 'en' | 'de'): SearchOperator | undefined {
  const canonical = keywordMap(locale)[normalizeKeyword(keyword)];
  return SEARCH_OPERATORS.find((operator) => operator === canonical);
}
